/**
 * Runtime v3 — canonical serialization and content digests.
 *
 * Identity of a definition, a plan, a manifest and a result is the digest of
 * its CANONICAL form: object keys in code-unit order, arrays in their given
 * order, no undefined members, no non-finite numbers. Two payloads that mean
 * the same thing therefore share an id, and a payload that changed by one
 * character cannot keep its old approval.
 *
 * The hash itself is INJECTED. This package stays free of `node:crypto` so it
 * remains client-reachable; the API and the CLI each pass the same SHA-256
 * implementation, and there is exactly one canonicalization for both.
 */

import type { ContentDigest, RuntimeV3JsonObject } from './common';
import type { RuntimeV3DefinitionContent, RuntimeV3DefinitionVersionRef } from './definitions';
import { RUNTIME_V3_REFUSAL_CODES, runtimeV3Accepted, runtimeV3Refused } from './refusals';
import type { RuntimeV3Outcome } from './refusals';

/** A SHA-256 over a UTF-8 string, returned as lowercase hex. */
export type RuntimeV3Sha256Hex = (input: string) => string;

export class RuntimeV3CanonicalJsonError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RuntimeV3CanonicalJsonError';
  }
}

function canonicalizeArray(value: readonly unknown[], path: string): string {
  const parts = value.map((entry, index) => canonicalize(entry, `${path}[${index}]`));
  return `[${parts.join(',')}]`;
}

function canonicalizeObject(value: object, path: string): string {
  const entries = Object.entries(value as Record<string, unknown>)
    .filter(([, entryValue]) => entryValue !== undefined)
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0));
  const parts = entries.map(
    ([key, entryValue]) => `${JSON.stringify(key)}:${canonicalize(entryValue, `${path}.${key}`)}`,
  );
  return `{${parts.join(',')}}`;
}

function canonicalizeNumber(value: number, path: string): string {
  if (!Number.isFinite(value)) {
    throw new RuntimeV3CanonicalJsonError(`non-finite number at ${path}`);
  }
  return JSON.stringify(value);
}

/**
 * Serialize one value canonically.
 *
 * Dispatch is a lookup on the runtime type, never a chain of comparisons on
 * the value's text: the engineering standard forbids control flow that
 * branches on string matching, and a serializer that sniffed contents would
 * be exactly that.
 */
function canonicalize(value: unknown, path: string): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return canonicalizeArray(value, path);
  const handlers: Readonly<Record<string, (input: unknown) => string>> = {
    string: (input) => JSON.stringify(input as string),
    number: (input) => canonicalizeNumber(input as number, path),
    boolean: (input) => JSON.stringify(input as boolean),
    object: (input) => canonicalizeObject(input as object, path),
  };
  const handler = handlers[typeof value];
  if (!handler) {
    throw new RuntimeV3CanonicalJsonError(`unserializable ${typeof value} at ${path}`);
  }
  return handler(value);
}

export function canonicalJsonStringify(value: unknown): string {
  return canonicalize(value, '$');
}

export function computeContentDigest(value: unknown, sha256Hex: RuntimeV3Sha256Hex): ContentDigest {
  return sha256Hex(canonicalJsonStringify(value));
}

/**
 * THE CANONICAL SHAPE OF ONE DEPENDENCY PIN, under the CURRENT (v2,
 * content-only) digest algorithm — exactly the fields that change what the
 * dependency means, whole or subset. Shared by `computeDefinitionDigest`
 * below and the current-algorithm subject in `digest-algorithms.ts`, so the
 * two can never drift apart on how a subset pin canonicalizes.
 *
 * A WHOLE pin carries `digest`; a SUBSET pin carries `subset` instead —
 * never both. `canonicalizeObject` already drops `undefined`-valued members
 * before hashing, so listing both fields here and leaving the absent one
 * `undefined` produces the identical canonical form a hand-written
 * whole-only shape always has: every digest computed for an existing
 * whole-pinned definition is unchanged by this function existing.
 */
export function canonicalDependencyPin(
  dependency: RuntimeV3DefinitionVersionRef,
): RuntimeV3JsonObject {
  return {
    definitionRef: dependency.definitionRef,
    scopeKind: dependency.scopeKind,
    tenantId: dependency.tenantId,
    digest: dependency.digest,
    subset: dependency.subset,
  };
}

/**
 * THE DIGEST IS THE DEFINITION'S IDENTITY, AND THE VERSION IS NOT IN IT.
 *
 * It covers exactly the content that gives a definition meaning — ref, kind,
 * scope, payload and the dependencies it pins. Lifecycle position, actors and
 * timestamps are outside it, so approving, activating and retiring never move
 * it.
 *
 * The VERSION is outside it too, and that is the whole point. The registry
 * assigns versions; an artifact does not carry one as a constraint. If the
 * version were digested, the same content authored into two databases whose
 * histories differ would hash differently — so the same definition would be a
 * different definition depending on where it landed, and publishing into a
 * fresh registry (a second tenant, a frozen build) could never be idempotent.
 * With the version out, identical content is identical everywhere, which is
 * what makes `author()` a no-op the second time.
 */
export function computeDefinitionDigest(
  content: RuntimeV3DefinitionContent,
  sha256Hex: RuntimeV3Sha256Hex,
): ContentDigest {
  return computeContentDigest(
    {
      definitionRef: content.definitionRef,
      kind: content.kind,
      scopeKind: content.scopeKind,
      tenantId: content.tenantId,
      payload: content.payload,
      dependencies: content.dependencies.map(canonicalDependencyPin),
    },
    sha256Hex,
  );
}

/** Plans and results share the same canonicalization, so a replay can compare digests. */
export function computePayloadDigest(
  payload: RuntimeV3JsonObject,
  sha256Hex: RuntimeV3Sha256Hex,
): ContentDigest {
  return computeContentDigest(payload, sha256Hex);
}

const LOWERCASE_SHA256_HEX = /^[0-9a-f]{64}$/;

export function isRuntimeV3ContentDigest(candidate: unknown): candidate is ContentDigest {
  return typeof candidate === 'string' && LOWERCASE_SHA256_HEX.test(candidate);
}

/**
 * WHY A SUBSET DIGEST CAN FAIL TO COMPUTE, as a closed set — never a thrown
 * string. `key_not_found` and `entry_identity_duplicated` name only the
 * `keys` actually at fault, never the whole collection: an unrelated entry's
 * absence or ambiguity does not touch a pin that never asked for it.
 */
export const RUNTIME_V3_SUBSET_DIGEST_FAILURE_REASONS = [
  /** `payload[collection]` is missing or not an array. */
  'collection_not_an_array',
  /** `keys` named no entry at all. */
  'keys_empty',
  /** the same key named twice. */
  'keys_duplicated',
  /** a requested key matches two or more entries — no single one to hash. */
  'entry_identity_duplicated',
  /** a requested key matches no entry in the collection. */
  'key_not_found',
] as const;
export type RuntimeV3SubsetDigestFailureReason =
  (typeof RUNTIME_V3_SUBSET_DIGEST_FAILURE_REASONS)[number];

const SUBSET_DIGEST_FAILURE_MESSAGES: Readonly<Record<RuntimeV3SubsetDigestFailureReason, string>> =
  Object.freeze({
    collection_not_an_array:
      'the depended-on payload does not carry this collection as an array',
    keys_empty: 'a subset pin must name at least one key',
    keys_duplicated: 'a subset pin may not repeat a key',
    entry_identity_duplicated:
      'two entries in this collection share one identity, so this key cannot address one of them',
    key_not_found: 'no entry in this collection carries this identity',
  });

function subsetDigestRefused(
  reason: RuntimeV3SubsetDigestFailureReason,
  collection: string,
  keys: readonly string[],
): RuntimeV3Outcome<ContentDigest> {
  return runtimeV3Refused(
    RUNTIME_V3_REFUSAL_CODES.unresolvedDefinitionVersion,
    SUBSET_DIGEST_FAILURE_MESSAGES[reason],
    { collection, keys, reason },
  );
}

function isRuntimeV3JsonRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const COLLECTION_ENTRY_IDENTITY_SUFFIX = 'Ref';

/**
 * THE ONE FIELD THAT IDENTIFIES A COLLECTION ENTRY, generically: the sole
 * field on it whose name ends `"Ref"` — the convention every keyed
 * collection in this registry already follows (`familyRef`,
 * `relationshipRef`, `definitionRef`, …). `null` when the entry is not an
 * object, or does not carry EXACTLY one such string field: an ambiguous or
 * unidentifiable entry is never guessed at, only ever unaddressable.
 */
function collectionEntryIdentityField(entry: unknown): string | null {
  if (!isRuntimeV3JsonRecord(entry)) return null;
  const candidates = Object.keys(entry).filter(
    (key) => key.endsWith(COLLECTION_ENTRY_IDENTITY_SUFFIX) && typeof entry[key] === 'string',
  );
  return candidates.length === 1 ? candidates[0] : null;
}

/** Every entry of `entries`, indexed by its identity field; `colliding` names identities two or more entries share. */
function indexCollectionByIdentity(
  entries: readonly unknown[],
): Readonly<{ byIdentity: ReadonlyMap<string, unknown>; colliding: ReadonlySet<string> }> {
  const byIdentity = new Map<string, unknown>();
  const colliding = new Set<string>();
  for (const entry of entries) {
    const identityField = collectionEntryIdentityField(entry);
    if (identityField === null) continue;
    const identity = (entry as Readonly<Record<string, unknown>>)[identityField] as string;
    if (byIdentity.has(identity)) colliding.add(identity);
    byIdentity.set(identity, entry);
  }
  return { byIdentity, colliding };
}

/**
 * THE SUBSET DIGEST — the one function that computes it, shared by the
 * re-pin tooling, the registry guards and their tests (never reimplemented
 * per call site, which is how the guard and the pin it checks could drift).
 *
 * "I depend only on the entries of `payload[collection]` whose identity is
 * in `keys`, and their canonical content hashes to this": the entries
 * matched by `keys` are sorted by their identity field before hashing, so
 * the result is stable under reordering `keys` or the source collection,
 * and unaffected by any entry not named in `keys` — adding, editing or
 * removing a sibling entry never changes it.
 */
export function computeCollectionSubsetDigest(
  payload: RuntimeV3JsonObject,
  collection: string,
  keys: readonly string[],
  sha256Hex: RuntimeV3Sha256Hex,
): RuntimeV3Outcome<ContentDigest> {
  const entries = payload[collection];
  if (!Array.isArray(entries)) {
    return subsetDigestRefused('collection_not_an_array', collection, keys);
  }
  if (keys.length === 0) {
    return subsetDigestRefused('keys_empty', collection, keys);
  }
  const duplicateKeys = [...new Set(keys.filter((key, index) => keys.indexOf(key) !== index))];
  if (duplicateKeys.length > 0) {
    return subsetDigestRefused('keys_duplicated', collection, duplicateKeys);
  }
  const { byIdentity, colliding } = indexCollectionByIdentity(entries);
  const collidingRequested = keys.filter((key) => colliding.has(key));
  if (collidingRequested.length > 0) {
    return subsetDigestRefused('entry_identity_duplicated', collection, collidingRequested);
  }
  const missing = keys.filter((key) => !byIdentity.has(key));
  if (missing.length > 0) {
    return subsetDigestRefused('key_not_found', collection, missing);
  }
  const matched = [...keys]
    .sort((left, right) => (left < right ? -1 : left > right ? 1 : 0))
    .map((key) => byIdentity.get(key));
  return runtimeV3Accepted(computeContentDigest(matched, sha256Hex));
}
